import { useCallback, useState } from 'react'
import { useStore, usePlayoutState, useWsConnected, useAlerts, useLiveSources } from '../store/index.js'
import { playoutApi, type LiveFillEntry, type LiveBreakRequest } from '../lib/api.js'

/**
 * Expose les actions de contrôle du playout et l'état temps réel.
 * Les actions API mettent à jour les flags `pending` dans le store
 * pour désactiver les boutons pendant la requête.
 */
export function usePlayout(channelId: string | null) {
  const [goLivePending, setGoLivePending] = useState(false)
  const [endLivePending, setEndLivePending] = useState(false)
  const [breakPending, setBreakPending] = useState(false)
  const playoutState  = usePlayoutState()
  const wsConnected   = useWsConnected()
  const alerts        = useAlerts()
  const liveSources   = useLiveSources()
  const {
    setSkipPending, setInterludePending, setEmergencyPending, setBugPending,
    skipPending, interludePending, emergencyPending, bugPending,
    activeBugType, setActiveBugType,
    addAlert,
  } = useStore()

  const manualInterludeActive = playoutState?.manualInterlude ?? false

  const _alertError = useCallback((action: string, err: unknown) => {
    const msg = err instanceof Error ? err.message : String(err)
    console.error(`[usePlayout] ${action} échoué :`, msg)
    addAlert({ type: 'LOAD_FAILURE', severity: 'ERROR', message: `${action} : ${msg}`, ts: new Date().toISOString() })
  }, [addAlert])

  const skip = useCallback(async () => {
    if (!channelId || skipPending) return
    setSkipPending(true)
    try {
      await playoutApi.skip(channelId)
    } catch (err) {
      _alertError('Skip', err)
    } finally {
      setSkipPending(false)
    }
  }, [channelId, skipPending, setSkipPending, _alertError])

  const goLive = useCallback(async (itemId: string) => {
    if (!channelId || goLivePending) return
    setGoLivePending(true)
    try {
      await playoutApi.goLive(channelId, itemId)
    } catch (err) {
      _alertError('Passage au direct', err)
      throw err
    } finally {
      setGoLivePending(false)
    }
  }, [channelId, goLivePending, _alertError])

  /**
   * Fin manuelle d'un direct : coupe le direct en cours et laisse le backend
   * décider du remplissage (infoservice / interlude) selon la fenêtre restante.
   * L'erreur est propagée pour l'affichage.
   */
  const endLive = useCallback(async (itemId: string) => {
    if (!channelId || endLivePending) return null
    setEndLivePending(true)
    try {
      return await playoutApi.endLive(channelId, itemId)
    } catch (err) {
      _alertError('Fin du direct', err)
      throw err
    } finally {
      setEndLivePending(false)
    }
  }, [channelId, endLivePending, _alertError])

  const skipAndReschedule = useCallback(async () => {
    if (!channelId || skipPending) return
    setSkipPending(true)
    try {
      await playoutApi.skipAndReschedule(channelId)
    } catch (err) {
      _alertError('Skip', err)
    } finally {
      setSkipPending(false)
    }
  }, [channelId, skipPending, setSkipPending, _alertError])

  /**
   * Fin anticipée de l'item à l'antenne : le termine et comble le trou laissé devant
   * le programme suivant, sans recaler la grille. L'erreur est propagée pour que
   * la boîte de dialogue puisse l'afficher et rester ouverte.
   */
  const endLiveAndFill = useCallback(async (
    itemId: string,
    items: LiveFillEntry[],
    options: { compact?: boolean; leadSec?: number; cutAt?: string; requireExactFill?: boolean } = {},
  ) => {
    if (!channelId || skipPending) return null
    setSkipPending(true)
    try {
      return await playoutApi.liveSkipFill(channelId, {
        itemId, items,
        compact:          options.compact,
        leadSec:          options.leadSec,
        cutAt:            options.cutAt,
        requireExactFill: options.requireExactFill,
      })
    } catch (err) {
      _alertError('Fin de direct + remplissage', err)
      throw err
    } finally {
      setSkipPending(false)
    }
  }, [channelId, skipPending, setSkipPending, _alertError])

  /**
   * Décrochage d'un bloc de direct : le segment à l'antenne est tronqué, le
   * comblage composé en régie est posé, et le direct reprend derrière jusqu'à la
   * fin du bloc. L'erreur est propagée pour que la boîte reste ouverte.
   */
  const breakLive = useCallback(async (body: LiveBreakRequest) => {
    if (!channelId || breakPending) return null
    setBreakPending(true)
    try {
      return await playoutApi.liveBreak(channelId, body)
    } catch (err) {
      _alertError('Décrochage du direct', err)
      throw err
    } finally {
      setBreakPending(false)
    }
  }, [channelId, breakPending, _alertError])

  /** Raccrochage : le direct reprend l'antenne plus tôt que le comblage prévu. */
  const resumeLive = useCallback(async (itemId?: string) => {
    if (!channelId || breakPending) return null
    setBreakPending(true)
    try {
      return await playoutApi.liveResume(channelId, itemId)
    } catch (err) {
      _alertError('Raccrochage du direct', err)
      throw err
    } finally {
      setBreakPending(false)
    }
  }, [channelId, breakPending, _alertError])

  /** Pendant un décrochage : prend le direct suivant de la grille (autre bloc). */
  const takeLive = useCallback(async (itemId: string, early = false) => {
    if (!channelId || breakPending) return null
    setBreakPending(true)
    try {
      return await playoutApi.liveTake(channelId, itemId, early)
    } catch (err) {
      _alertError('Prise du direct suivant', err)
      throw err
    } finally {
      setBreakPending(false)
    }
  }, [channelId, breakPending, _alertError])

  /** Direct en ERREUR ou non parti : met sa source à l'antenne maintenant. */
  const startLiveNow = useCallback(async (itemId: string) => {
    if (!channelId || goLivePending) return null
    setGoLivePending(true)
    try {
      return await playoutApi.startLiveNow(channelId, itemId)
    } catch (err) {
      _alertError('Lancement de la source', err)
      throw err
    } finally {
      setGoLivePending(false)
    }
  }, [channelId, goLivePending, _alertError])

  const toggleInterlude = useCallback(async (clipId?: string) => {
    if (!channelId || interludePending) return
    setInterludePending(true)
    try {
      const newState = !manualInterludeActive
      await playoutApi.forceInterlude(channelId, clipId, newState)
      // On ne met pas à jour l'état local, on attend le retour WebSocket/PlayoutState
    } catch (err) {
      _alertError('Interlude', err)
    } finally {
      setInterludePending(false)
    }
  }, [channelId, interludePending, manualInterludeActive, _alertError])

  const emergencyStop = useCallback(async () => {
    if (!channelId || emergencyPending) return
    // Validation obligatoire : l'arrêt coupe l'antenne pour tout le monde et
    // bascule sur la boucle de secours. Un clic accidentel ne doit jamais le
    // déclencher seul.
    const confirmed = window.confirm(
      'Arrêt de l’antenne pour tout le monde ?\n\n'
      + 'La diffusion en cours est coupée et remplacée par la boucle de secours '
      + '(mode incident). Pour reprendre ensuite, appuyez sur « Interlude » pour '
      + 'revenir au playout normal (la grille repart sur l’élément suivant).',
    )
    if (!confirmed) return
    setEmergencyPending(true)
    try {
      await playoutApi.emergencyStop(channelId)
    } catch (err) {
      _alertError('Arrêt urgence', err)
    } finally {
      setEmergencyPending(false)
    }
  }, [channelId, emergencyPending, setEmergencyPending, _alertError])

  const toggleBug = useCallback(async (bugType?: string) => {
    if (!channelId || bugPending) return
    setBugPending(true)
    try {
      if (activeBugType && !bugType) {
        await playoutApi.hideBug(channelId)
        setActiveBugType(null)
      } else {
        await playoutApi.showBug(channelId, bugType)
        setActiveBugType(bugType ?? null)
      }
    } catch (err) {
      _alertError('Logo', err)
    } finally {
      setBugPending(false)
    }
  }, [channelId, bugPending, activeBugType, setActiveBugType, setBugPending, _alertError])

  const setBug = useCallback(async (bugType: string) => {
    if (!channelId || bugPending) return
    setBugPending(true)
    try {
      if (bugType === 'NONE') {
        await playoutApi.hideBug(channelId)
        setActiveBugType(null)
      } else {
        await playoutApi.showBug(channelId, bugType)
        setActiveBugType(bugType)
      }
    } catch (err) {
      _alertError('Logo', err)
    } finally {
      setBugPending(false)
    }
  }, [channelId, bugPending, setActiveBugType, setBugPending, _alertError])

  const startBridge = useCallback(async () => {
    if (!channelId) return
    try {
      await playoutApi.start(channelId)
    } catch (err) {
      _alertError('Démarrage CasparCG', err)
      throw err
    }
  }, [channelId, _alertError])

  const stopBridge = useCallback(async () => {
    if (!channelId) return
    try {
      await playoutApi.stop(channelId)
    } catch (err) {
      _alertError('Arrêt CasparCG', err)
      throw err
    }
  }, [channelId, _alertError])

  return {
    // État
    playoutState,
    wsConnected,
    alerts,
    liveSources,
    // Actions
    skip,
    goLive,
    endLive,
    skipAndReschedule,
    endLiveAndFill,
    breakLive,
    resumeLive,
    takeLive,
    startLiveNow,
    toggleInterlude,
    emergencyStop,
    toggleBug,
    setBug,
    startBridge,
    stopBridge,
    // Flags
    skipPending,
    goLivePending,
    endLivePending,
    breakPending,
    interludePending,
    emergencyPending,
    bugPending,
    activeBugType,
    manualInterludeActive,
    bugVisible: !!activeBugType,
    // Raccourcis
    isPlaying:   playoutState?.state === 'PLAYING',
    isIdle:      !playoutState || playoutState.state === 'IDLE',
    hasGap:      playoutState?.state === 'GAP',
    hasError:    playoutState?.state === 'ERROR',
  }
}
